import React from "react";
import { Globe, ChevronRight, MapPin } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import JurisdictionSelector from "../JurisdictionSelector";
import { getTotalJurisdictions } from "../../../utils/jurisdictionData";

const SearchHeader = ({
    showJurisdictionExplorer,
    setShowJurisdictionExplorer,
    setAddress,
    setApn,
    setLookupBy,
    onSelectCity,
    selectedState,
    selectedCity,
    onStateSwitch,
}) => {
    return (
        <div className="bg-gradient-to-br from-indigo-700 via-blue-600 to-blue-700 px-4 py-3 rounded-t-2xl relative overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />

            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-3">
                {/* Left — Title + State Segmented Control */}
                <div className="flex items-center gap-4">
                    <div>
                        <h2 className="text-xl font-black text-white tracking-tight">
                            Property Analysis
                        </h2>
                        <p className="text-blue-100/70 text-[11px] font-medium leading-tight">
                            GIS analysis with real-time zoning and terrain data.
                        </p>
                    </div>

                    {/* Segmented State Control */}
                    <div className="hidden sm:flex items-center bg-white/[0.08] rounded-lg p-[3px] border border-white/[0.08]">
                        {[
                            { code: "CA", label: "California" },
                            { code: "TX", label: "Texas" },
                        ].map((tab) => {
                            const isActive = selectedState === tab.code;
                            return (
                                <button
                                    key={tab.code}
                                    onClick={() => onStateSwitch(tab.code)}
                                    className={`relative px-3 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-widest transition-colors duration-200 ${
                                        isActive
                                            ? "text-white"
                                            : "text-white/35 hover:text-white/60"
                                    }`}
                                >
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeState"
                                            className="absolute inset-0 bg-white/15 rounded-md shadow-sm"
                                            transition={{ type: "spring", stiffness: 500, damping: 32 }}
                                        />
                                    )}
                                    <span className="relative">{tab.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Active target chip */}
                    <div className="hidden md:flex items-center gap-1 px-2 py-1 bg-emerald-500/15 rounded-md border border-emerald-400/20">
                        <MapPin className="w-2.5 h-2.5 text-emerald-400" />
                        <span className="text-[9px] font-bold text-emerald-300/90 uppercase tracking-wider">
                            {selectedCity}
                        </span>
                    </div>
                </div>

                {/* Right — Service Network Badge */}
                <button
                    onClick={() => setShowJurisdictionExplorer(!showJurisdictionExplorer)}
                    className="flex items-center gap-2.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-md rounded-xl transition-all group"
                >
                    <div className="relative">
                        <Globe className="w-3.5 h-3.5 text-blue-300 group-hover:rotate-12 transition-transform" />
                        <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse shadow-[0_0_6px_#10b981]" />
                    </div>
                    <div className="text-left">
                        <span className="block text-[8px] font-black text-white/50 uppercase tracking-widest leading-none">Service Network</span>
                        <span className="block text-[10px] font-bold text-white uppercase leading-none mt-0.5">{getTotalJurisdictions()} Verified Cities</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 text-white/30 transition-transform ${showJurisdictionExplorer ? 'rotate-90' : ''}`} />
                </button>
            </div>

            {/* Collapsible Jurisdiction Explorer */}
            <AnimatePresence>
                {showJurisdictionExplorer && (
                    <motion.div
                        initial={{ height: 0, opacity: 0, marginTop: 0 }}
                        animate={{ height: "auto", opacity: 1, marginTop: 24 }}
                        exit={{ height: 0, opacity: 0, marginTop: 0 }}
                        className="overflow-hidden"
                    >
                        <JurisdictionSelector
                            onSelectCity={(city, county) => {
                                if (onSelectCity) {
                                    onSelectCity(city, county);
                                }
                                setShowJurisdictionExplorer(false);
                            }}
                            activeState={selectedState}
                            className="bg-black/20 border-white/10"
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SearchHeader;
